import React from 'react';
import { useTranslation } from 'react-i18next';
import { Check } from 'lucide-react';
import { cn } from '@/lib/utils';

const STEPS = [{ id: 1 }, { id: 2 }, { id: 3 }];

interface PropertyCreateProgressProps {
  currentStep: number;
}

export function PropertyCreateProgress({
  currentStep,
}: PropertyCreateProgressProps) {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';

  return (
    <div className="mb-8">
      {/* Mobile view - show only current step */}
      <div
        className={cn(
          'md:hidden flex items-center justify-center',
          isRTL ? 'flex-row-reverse' : 'flex-row'
        )}
      >
        <div
          className={cn(
            'flex items-center',
            isRTL ? 'flex-row-reverse' : 'flex-row'
          )}
        >
          <div className="w-10 h-10 rounded-full flex items-center justify-center text-sm font-medium bg-primary text-primary-foreground">
            {currentStep}
          </div>
          <div className={cn(isRTL ? 'mr-3' : 'ml-3')}>
            <div
              className={cn(
                'text-sm font-medium text-foreground',
                isRTL ? 'text-right' : 'text-left'
              )}
            >
              {t(`properties.create.steps.${currentStep}.title`)}
            </div>
            <div
              className={cn(
                'text-xs text-muted-foreground',
                isRTL ? 'text-right' : 'text-left'
              )}
            >
              {t('properties.create.stepProgress', {
                current: currentStep,
                total: 3,
              })}
            </div>
          </div>
        </div>
      </div>

      {/* Desktop view - show all steps */}
      <div
        className={cn(
          'hidden md:flex items-center',
          isRTL ? 'flex-row-reverse' : 'flex-row'
        )}
      >
        {STEPS.map((step, index) => (
          <React.Fragment key={step.id}>
            <div
              className={cn(
                'flex items-center',
                isRTL ? 'flex-row-reverse' : 'flex-row'
              )}
            >
              <div
                className={cn(
                  'w-10 h-10 rounded-full flex items-center justify-center text-sm font-medium',
                  currentStep > step.id
                    ? 'bg-green-500 text-white'
                    : currentStep === step.id
                      ? 'bg-primary text-primary-foreground'
                      : 'bg-muted text-muted-foreground'
                )}
              >
                {currentStep > step.id ? (
                  <Check className="w-5 h-5" />
                ) : (
                  step.id
                )}
              </div>
              <div className={cn('ml-3 mr-3', isRTL && 'ml-3 mr-3')}>
                <div
                  className={cn(
                    'text-sm font-medium',
                    isRTL ? 'text-right' : 'text-left',
                    currentStep >= step.id
                      ? 'text-foreground'
                      : 'text-muted-foreground'
                  )}
                >
                  {t(`properties.create.steps.${step.id}.title`)}
                </div>
                <div
                  className={cn(
                    'text-xs text-muted-foreground',
                    isRTL ? 'text-right' : 'text-left'
                  )}
                >
                  {t(`properties.create.steps.${step.id}.description`)}
                </div>
              </div>
            </div>
            {index < STEPS.length - 1 && (
              <div
                className={cn(
                  'flex-1 h-px mx-4',
                  currentStep > step.id ? 'bg-green-500' : 'bg-muted'
                )}
              />
            )}
          </React.Fragment>
        ))}
      </div>
    </div>
  );
}
